import * as React from "react";
import { LinksDialogContext } from "components/flexComponents/Links/GridView/LinkDialogContext";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import Links from "components/flexComponents/Links/Links";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkIcon } from "uitk-react-icons";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkButton } from "uitk-react-button";
import { UitkLink, UitkLinkAlign } from "uitk-react-link";
import { FlexComponent } from "typings/flexFramework/FlexComponent";
import { withStores } from "stores";
import { observer } from "mobx-react";
import { DefaultFlexModuleResult } from "src/typings/microserviceModels/links-flex-module";
import { flexComponentToFlexTemplateNode, DescendantDepth } from "src/stores/FlexTemplateStore";
import { DialogProps, LinksFTNode, LinksGridItemProps } from "../typings";

export const LinksGridItem = withStores("flexModuleModelStore")(
  observer((props: LinksGridItemProps) => {
    const { view, group, routeStore, id, flexModuleModelStore, setLinkRef } = props;
    const { modules, model } = group;
    const linksDialogContext = LinksDialogContext;
    const { title, subheading, dialogHeading, subHeadingAlignment } = model;
    const linkModules: LinksFTNode[] = modules
      .filter((module: FlexComponent) => {
        const componentModel = flexModuleModelStore.getModel(module.id) as DefaultFlexModuleResult | null;

        return componentModel?.name === "links";
      })
      .map((module: FlexComponent) => flexComponentToFlexTemplateNode(module, DescendantDepth.NONE));

    const openDialog = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
      event.preventDefault();
      linksDialogContext.dialogHeading = dialogHeading;
      linksDialogContext.modules = linkModules;
      routeStore.updateSearch({ pwaDialog: `linksDialog-${id}` });
    };

    const keyHelper = new ItemKeyHelper(`LinksGridItem-${id}`);

    const links = linkModules?.map((module) => (
      <div aria-hidden="true" hidden key={keyHelper.next()}>
        <Links flexTemplateNode={module} key={keyHelper.next()} />
      </div>
    ));

    const dialogProps: DialogProps = { title, subheading, subHeadingAlignment, openDialog, setLinkRef };

    let dialogView;
    if (view === "row-trigger") {
      dialogView = <DialogViewWithIcon {...dialogProps} />;
    } else if (view === "standard-link") {
      dialogView = <DialogViewLinkableText {...dialogProps} />;
    }

    return (
      <div className="LinksGridItem" key={keyHelper.next()}>
        <UitkCardAloha className={view}>{dialogView}</UitkCardAloha>
        <div className="is-visually-hidden">{links}</div>
      </div>
    );
  })
);

const setReferenceOpener = (
  setLinkRef: (ref: React.RefObject<HTMLElement>) => void,
  elementRef: React.RefObject<HTMLElement>
) => {
  React.useEffect(() => {
    setLinkRef(elementRef);
  }, []);
};

const DialogViewWithIcon: React.FC<DialogProps> = observer(({ title, subheading, openDialog, setLinkRef }) => {
  const buttonRef = React.createRef<HTMLButtonElement>();

  setReferenceOpener(setLinkRef, buttonRef);

  return (
    <UitkCardAlohaContentSection>
      <UitkButton onClick={openDialog} isFullWidth aria-label={title} domRef={buttonRef}>
        <UitkLayoutFlex justifyContent="space-between" alignItems="center" className="LinksGridItem">
          <UitkLayoutFlexItem>
            <div className="uitk-type-left">
              <UitkHeading tag="h3" size={7}>
                {title}
              </UitkHeading>
              {subheading && <UitkSubheading>{subheading}</UitkSubheading>}
            </div>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <UitkIcon name="chevron_right" />
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>
      </UitkButton>
    </UitkCardAlohaContentSection>
  );
});

const DialogViewLinkableText: React.FC<DialogProps> = observer(
  ({ title, subheading, subHeadingAlignment, openDialog, setLinkRef }) => {
    const linkRef = React.createRef<HTMLAnchorElement>();

    setReferenceOpener(setLinkRef, linkRef);

    return (
      <UitkCardAlohaContentSection>
        <UitkLayoutFlexItem>
          <UitkLayoutFlex justifyContent={subHeadingAlignment === "center" ? "center" : "start"}>
            <UitkLayoutFlexItem className={subHeadingAlignment === "center" ? "links-item-center" : ""}>
              <div>
                <UitkHeading tag="h3" size={7}>
                  {title}
                </UitkHeading>
                <UitkLink
                  onClick={openDialog}
                  align={subHeadingAlignment === "center" ? UitkLinkAlign.CENTER : UitkLinkAlign.LEFT}
                >
                  <a href="" role="button" ref={linkRef}>
                    {subheading}
                  </a>
                </UitkLink>
              </div>
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkLayoutFlexItem>
      </UitkCardAlohaContentSection>
    );
  }
);

export default LinksGridItem;
